import React from "react";
import type { MRT_ColumnDef } from "material-react-table";
import { formatGBP, isFormattableAmount, parseGBP } from "../../utils/currency";
import { isMonetaryField } from "../../shared/monetaryFields";

/**
 * Renders a monetary cell value. Non-numeric content (a dash, "N/A", a status
 * string) is passed through untouched so generic grids do not mangle it.
 */
export const CurrencyCell: React.FC<{
  value: unknown;
  blank?: string;
  currency?: string;
}> = ({ value, blank, currency }) =>
  isFormattableAmount(value) ? (
    <>{formatGBP(value as number | string, { blank, currency })}</>
  ) : (
    <>{(value as React.ReactNode) ?? blank ?? ""}</>
  );

/**
 * Header styling shared by every right-aligned monetary column.
 *
 * Exported so columns that build their own `Cell` (and therefore never pass
 * through `currencyColumn`) can match — the two editable "Actual" columns in
 * the commission grids do exactly that.
 */
export const CURRENCY_HEAD_CELL_SX = {
  "& .Mui-TableHeadCell-Content": {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
} as const;

export type CurrencyColumnOptions = {
  /** Rendered when the value is absent. Defaults to "". */
  blank?: string;
  /** Row property holding an ISO currency code, when the row is not GBP. */
  currencyField?: string;
};

/**
 * Sorts on the amount, whatever shape it arrives in.
 *
 * The API returns SQL DECIMAL columns as JSON strings ("1000.00"), and a plain
 * `a > b` comparison sorts those alphabetically — "1000.00" before "556.00".
 * `parseGBP` also copes with the grouped/prefixed text that monetary `type:
 * "text"` fields hold. Rows with no amount at all sort together at the bottom
 * ascending rather than scattering through the numbers.
 */
const amountSortingFn = <T,>(
  rowA: any,
  rowB: any,
  columnId: string,
): number => {
  const a = parseGBP(rowA.getValue(columnId));
  const b = parseGBP(rowB.getValue(columnId));

  if (a === null && b === null) return 0;
  if (a === null) return -1;
  if (b === null) return 1;
  return a === b ? 0 : a > b ? 1 : -1;
};

/**
 * Builds a material-react-table column that DISPLAYS UK Sterling while leaving the
 * underlying accessor value untouched.
 *
 * Only `Cell` (the renderer) is overridden — never `accessorFn` — so column
 * filtering, aggregation and row totals keep operating on the raw value.
 * Formatting the accessor instead would sort "£1,000,000.00" below "£556.00".
 *
 * `filterVariant` is deliberately NOT set: forcing a range filter would swap the
 * text box users have today for a min/max pair on every monetary column, and any
 * string-based `filterFn` a table supplies from config would then be handed
 * `[min, max]` instead of a string. Tables opt into range filtering themselves.
 */
export const currencyColumn = <T extends Record<string, any>>(
  accessorKey: string,
  header: string,
  overrides: Partial<MRT_ColumnDef<T>> = {},
  options: CurrencyColumnOptions = {},
): MRT_ColumnDef<T> =>
  ({
    accessorKey,
    header,
    sortingFn: amountSortingFn,
    muiTableBodyCellProps: { align: "right" },
    muiTableHeadCellProps: {
      align: "right",
      // MRT flips the header's content to `row-reverse` whenever align is
      // "right", which also reverses the icons after the label — so the drag
      // handle lands on the far LEFT of a monetary header while every other
      // column keeps it on the right. Restoring `row` and packing to the end
      // keeps the label right-aligned with its handle still on the right.
      sx: CURRENCY_HEAD_CELL_SX,
    },
    Cell: ({ cell, row }: any) => (
      <CurrencyCell
        value={cell.getValue()}
        blank={options.blank}
        currency={
          options.currencyField
            ? row?.original?.[options.currencyField]
            : undefined
        }
      />
    ),
    ...overrides,
  }) as MRT_ColumnDef<T>;

/**
 * Applies currency rendering to columns declared as plain data (tableConfig.json,
 * or arrays built from config), by consulting the monetary-field registry.
 *
 * A column may opt in or out explicitly with `format: "currency"` / `format: "none"`;
 * otherwise the registry decides. Columns that already define their own `Cell` are
 * left alone so bespoke renderers are never clobbered.
 */
export const withCurrencyColumns = <T extends Record<string, any>>(
  columns: Array<MRT_ColumnDef<T> & { format?: string }>,
  table?: string,
): Array<MRT_ColumnDef<T>> =>
  columns.map((column) => {
    const key = (column as any).accessorKey as string | undefined;
    if (!key || column.Cell) return column;

    const explicit = column.format;
    const monetary =
      explicit === "currency" ||
      (explicit !== "none" && isMonetaryField(key, table));
    if (!monetary) return column;

    const { format, ...rest } = column;
    return currencyColumn<T>(key, String(column.header ?? key), rest);
  });

/**
 * An amount cell that reads as money at rest and edits as a raw number.
 *
 * `<input type="number">` cannot show "£1,234.50" — the HTML spec restricts its
 * value to a floating-point number, so handing it a formatted string blanks the
 * control. An editable amount therefore cannot be formatted in place. This
 * renders the formatted value as text until the user clicks or tabs into it,
 * then hands over to the caller's own input for entry.
 *
 * That split is also what the v2 architecture asks for: amounts are painted in
 * view/read-only mode and stay raw while being edited, so nothing formatted
 * ever reaches component state or a payload.
 *
 * The caller keeps its own input and all of its behaviour (guards, validation,
 * save-on-blur); it only has to spread `editProps` so the field takes focus on
 * entry and returns to the formatted view on blur.
 */
export const CurrencyEditableCell: React.FC<{
  value: unknown;
  renderInput: (editProps: {
    autoFocus: boolean;
    onBlur: () => void;
  }) => React.ReactNode;
  /** Rendered when there is no amount. Defaults to "". */
  blank?: string;
  /**
   * When supplied, the at-rest view honours `amount_flag` — a percentage shows
   * "%" instead of a currency symbol. Editing is unaffected either way: the
   * input always holds the raw number.
   */
  amountFlag?: unknown;
}> = ({ value, renderInput, blank, amountFlag }) => {
  const [editing, setEditing] = React.useState(false);

  if (editing) {
    return <>{renderInput({ autoFocus: true, onBlur: () => setEditing(false) })}</>;
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Edit amount"
      style={{
        textAlign: "right",
        cursor: "text",
        width: "100%",
        minHeight: "32px",
        lineHeight: "32px",
      }}
      onClick={() => setEditing(true)}
      // Keyboard users tab onto the cell itself, so entering edit mode has to
      // work without a pointer.
      onFocus={() => setEditing(true)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          setEditing(true);
        }
      }}
    >
      <AmountOrPercentCell value={value} amountFlag={amountFlag} blank={blank} />
    </div>
  );
};

/**
 * Commission rows carry money and percentages in the same `amount` column, told
 * apart by `amount_flag`: 1 means percentage, 0 (or absent) means a fixed money
 * amount.
 *
 * Coerced rather than compared strictly, because the flag reaches the UI in
 * several shapes across endpoints and mappings — `1`, `"1"`, and `"1.0"` from
 * a DECIMAL column all mean percentage. A strict `=== 1` quietly treats the
 * string forms as money and puts a £ on a percentage.
 *
 * Absent stays money: `null`, `undefined` and `""` all coerce to 0 or NaN,
 * which is the correct default — an amount with no flag is a money amount.
 */
export const isPercentageAmount = (amountFlag: unknown): boolean => {
  if (amountFlag === null || amountFlag === undefined || amountFlag === "") {
    return false;
  }
  return Number(amountFlag) === 1;
};

/**
 * Renders that shared column: Sterling when the row is money, the raw value with
 * a `%` suffix when it is a percentage.
 *
 * A percentage must never take a currency symbol — "£5" and "5%" of a commission
 * are wildly different numbers, so getting this wrong misstates what a party is
 * owed rather than just looking untidy.
 */
export const AmountOrPercentCell: React.FC<{
  value: unknown;
  amountFlag: unknown;
  blank?: string;
}> = ({ value, amountFlag, blank }) =>
  isPercentageAmount(amountFlag) ? (
    <>
      {value as React.ReactNode} <b>%</b>
    </>
  ) : (
    <CurrencyCell value={value} blank={blank} />
  );
